import {
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { apiService } from '../../services/api';

interface Milestone {
  id: string;
  title: string;
  due_on: string;
  status: 'planned' | 'in_progress' | 'completed' | 'overdue';
  notes: string;
  created_at: string;
  updated_at: string;
}

interface Project {
  id: string;
  name: string;
  goal: string;
  status: string;
  created_at: string;
}

interface TrackerOverview {
  project: Project;
  latest_self_check: Record<string, unknown> | null;
  latest_env_snapshot: Record<string, unknown> | null;
  next_milestone: Record<string, unknown> | null;
  recent_runs: Array<Record<string, unknown>>;
  open_errors: Array<Record<string, unknown>>;
  active_tasks: Array<Record<string, unknown>>;
}

const RoadmapPage: React.FC = () => {
  const {
    data: overview,
    isLoading,
    error,
  } = useQuery<TrackerOverview>({
    queryKey: ['tracker-overview'],
    queryFn: async (): Promise<TrackerOverview> => {
      const response = await apiService.get('/tracker/overview');
      return response as TrackerOverview;
    },
    refetchInterval: 30000,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'overdue':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5" />;
      case 'in_progress':
        return <ClockIcon className="h-5 w-5" />;
      case 'overdue':
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      default:
        return <FlagIcon className="h-5 w-5" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading roadmap data</h3>
              <p className="mt-2 text-sm text-red-700">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const project = overview?.project;
  const milestones: Milestone[] = [
    // Active/Planned Phases
    {
      id: 'smart-defaults',
      title: 'Smart Defaults Audit',
      due_on: '2026-02-08T00:00:00Z',
      status: 'in_progress' as const,
      notes:
        'Audit current defaults in Deal + BHPH settings. Map local settings to backend schema. Deal defaults complete, BHPH pending.',
      created_at: '2026-02-04T00:00:00Z',
      updated_at: '2026-02-06T00:00:00Z',
    },
    {
      id: 'phase-3.1',
      title: 'Phase 3.1: Deal Calculator Enhancement',
      due_on: '2026-02-10T00:00:00Z',
      status: 'planned' as const,
      notes:
        'Real-time payment calculations with fees, credit tier integration, APR determination. Data dependencies resolved (718 vehicles, 3,157 customers).',
      created_at: '2026-02-01T00:00:00Z',
      updated_at: '2026-02-06T00:00:00Z',
    },
    {
      id: 'website-builder',
      title: 'Website Builder Platform',
      due_on: '2026-02-20T00:00:00Z',
      status: 'planned' as const,
      notes:
        '5 responsive templates, auto-sync inventory, dealer customization (colors, logos, contact).',
      created_at: '2026-02-01T00:00:00Z',
      updated_at: '2026-02-04T00:00:00Z',
    },
    {
      id: 'bhph-tests',
      title: 'BHPH Integration Tests',
      due_on: '2026-02-15T00:00:00Z',
      status: 'planned' as const,
      notes:
        'Complete test suite for BHPH operations, payment processing workflows, collection workflows.',
      created_at: '2026-02-01T00:00:00Z',
      updated_at: '2026-02-06T00:00:00Z',
    },
    // Recently Completed Phases
    {
      id: 'marketplace-integrations',
      title: 'Marketplace Integrations',
      due_on: '2026-02-04T00:00:00Z',
      status: 'completed' as const,
      notes:
        'All free platform adapters (Facebook, Craigslist, OfferUp, Google), paid platform stubs, MarketplaceBlaster orchestration, credential management UI.',
      created_at: '2026-02-02T00:00:00Z',
      updated_at: '2026-02-04T00:00:00Z',
    },
    {
      id: 'phase-1.9',
      title: 'Phase 1.9: Stocking Guide Module',
      due_on: '2026-02-01T00:00:00Z',
      status: 'completed' as const,
      notes:
        'StockingGuidePage with full data integration, dashboard with 5 analysis sections, advanced features (export, date range, auto-refresh, drag-and-drop).',
      created_at: '2026-01-28T00:00:00Z',
      updated_at: '2026-02-01T00:00:00Z',
    },
    {
      id: 'phase-1.12',
      title: 'Phase 1.12: Inventory Import UX',
      due_on: '2026-01-25T00:00:00Z',
      status: 'completed' as const,
      notes:
        'Settings > Inventory with In-House/Third-Party selector, mobile pages (VIN scanner, photo capture), CarsForSale.com integration.',
      created_at: '2026-01-20T00:00:00Z',
      updated_at: '2026-01-25T00:00:00Z',
    },
    {
      id: 'phase-1.11',
      title: 'Phase 1.11: Dark Mode & Preferences',
      due_on: '2026-01-24T00:00:00Z',
      status: 'completed' as const,
      notes:
        'Dark mode applied to layout components, PreferencesContext for centralized state, compact table view.',
      created_at: '2026-01-20T00:00:00Z',
      updated_at: '2026-01-24T00:00:00Z',
    },
    {
      id: 'phase-1.10',
      title: 'Phase 1.10: Profile Management',
      due_on: '2026-01-23T00:00:00Z',
      status: 'completed' as const,
      notes:
        'Comprehensive profile page with 4 tabs, avatar management system (18 built-in + upload), 2FA setup, localStorage persistence.',
      created_at: '2026-01-20T00:00:00Z',
      updated_at: '2026-01-23T00:00:00Z',
    },
  ];

  const completedMilestones = milestones.filter((m) => m.status === 'completed');
  const activeMilestones = milestones.filter((m) => m.status !== 'completed');
  const progressPercentage = Math.round((completedMilestones.length / milestones.length) * 100);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Roadmap</h1>
          <p className="text-gray-600">Milestone timeline and project progress</p>
        </div>
        <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700">
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Milestone
        </button>
      </div>

      {/* Project Overview */}
      {project && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{project.name}</h2>
              <p className="text-gray-600">{project.goal}</p>
            </div>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}
            >
              {project.status}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Overall Progress</span>
              <span>{progressPercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Milestones Timeline */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Milestone Timeline</h3>
        </div>

        <div className="divide-y divide-gray-200">
          {activeMilestones.map((milestone) => {
            const daysUntilDue = getDaysUntilDue(milestone.due_on);
            const isOverdue = daysUntilDue < 0;
            const isDueSoon = daysUntilDue <= 7 && daysUntilDue >= 0;

            return (
              <div key={milestone.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className={`flex-shrink-0 ${getStatusColor(milestone.status)}`}>
                      {getStatusIcon(milestone.status)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-medium text-gray-900">{milestone.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{milestone.notes}</p>
                      <div className="flex items-center mt-2 space-x-4">
                        <div className="flex items-center text-sm text-gray-500">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          Due: {formatDate(milestone.due_on)}
                        </div>
                        <div
                          className={`text-sm font-medium ${
                            isOverdue
                              ? 'text-red-600'
                              : isDueSoon
                                ? 'text-yellow-600'
                                : 'text-gray-600'
                          }`}
                        >
                          {isOverdue
                            ? `${Math.abs(daysUntilDue)} days overdue`
                            : isDueSoon
                              ? `${daysUntilDue} days left`
                              : `${daysUntilDue} days remaining`}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0 ml-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(milestone.status)}`}
                    >
                      {milestone.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {activeMilestones.length === 0 && (
          <div className="text-center py-12">
            <FlagIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No active milestones</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first milestone.
            </p>
          </div>
        )}
      </div>

      {/* Completed Milestones */}
      {completedMilestones.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Completed Milestones</h3>
          </div>

          <div className="divide-y divide-gray-200">
            {completedMilestones.map((milestone) => (
              <div key={milestone.id} className="p-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 text-green-600">
                    <CheckCircleIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-medium text-gray-900">{milestone.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{milestone.notes}</p>
                    <div className="flex items-center mt-2 text-sm text-gray-500">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      Completed: {formatDate(milestone.updated_at)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RoadmapPage;
